import { makeStyles } from '@material-ui/core/styles'
import { clearAfter, disableUserSelect, outlineOnFocus } from 'swisscard/components/common/mixins'
import { IconSpinner } from 'swisscard/images/icons'

import type { Theme } from '@material-ui/core/styles'

const buttonTheming = (theme: Theme) => ({
  borderRadius: theme.customButtons.borderRadius,
  textTransform: theme.customButtons.textTransform,
  letterSpacing: theme.customButtons.letterSpacing,
  fontWeight: theme.customButtons.fontWeight,
  fontStyle: theme.customButtons.fontStyle,
  fontSize: theme.customButtons.fontSize ? theme.customButtons.fontSize : 16,
})

export const buttonStyles = makeStyles((theme: Theme) => ({
  // rotating animation
  '@keyframes rotating': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },

  // buttons wrapper
  buttonsWrapper: {
    marginTop: -8 + 60 - 27,
    marginLeft: -8,

    [theme.breakpoints.down('xs')]: {
      marginLeft: 0,
    },

    '& > *': {
      float: 'left',
      marginTop: 8,
      marginLeft: 8,

      [theme.breakpoints.down('xs')]: {
        marginLeft: 0,
      },
    },

    ...clearAfter,
  },

  // next button
  buttonNext: {
    float: 'right',
    position: 'relative',

    [theme.breakpoints.down('sm')]: {
      left: 0,
    },

    [theme.breakpoints.down('xs')]: {
      float: 'none',
      width: '100%',
    },
  },

  // back button
  buttonBack: {
    float: 'left',

    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },

  // single buttons
  button: {
    ...disableUserSelect,
    ...outlineOnFocus,
    width: 'auto',
    webkitAppearance: 'none',
    mozAppearance: 'none',
    overflow: 'hidden',
    display: 'inline-block',
    position: 'relative',
    textDecoration: 'none',
    cursor: 'pointer',
    minWidth: 200,
    textAlign: 'center',
    transitionTimingFunction: 'ease-in-out',
    transitionProperty: 'background-color, border-color, color',
    transitionDuration: '0.2s',
    borderStyle: 'solid',
    borderWidth: 2,
    borderColor: theme.customColors.button.secondary.default.border,
    backgroundColor: theme.customColors.button.secondary.default.bg,
    color: theme.customColors.button.secondary.default.text,
    height: 'auto',
    padding: '18px',
    marginBottom: 5,
    lineHeight: 1.5,
    ...buttonTheming(theme),

    '@media only screen and (max-width:599px)': {
      padding: '11px 20px',
      width: '100%',
      maxWidth: 'none',
    },
    '@media only screen and (min-width: 600px) and (max-width: 768px)': {
      fontSize: 14,
      padding: '6px 20px',
    },
    '@media only screen and (min-width: 769px) and (max-width: 1023px)': {
      fontSize: 14,
      padding: '6px 20px',
    },
    '@media only screen and (min-width: 1024px) and (max-width: 1439px)': {
      fontSize: 16,
      padding: '10px 20px',
    },
    '@media only screen and (min-width: 1440px)': {
      fontSize: 20,
      padding: '13px 20px',
    },

    '&:hover': {
      borderColor: theme.customColors.button.secondary.hover.border,
      backgroundColor: theme.customColors.button.secondary.hover.bg,
      color: theme.customColors.button.secondary.hover.text,
    },
    '&:focus': {
      borderColor: theme.customColors.button.secondary.focus.border,
      backgroundColor: theme.customColors.button.secondary.focus.bg,
      color: theme.customColors.button.secondary.focus.text,
    },
    '&:active': {
      borderColor: theme.customColors.button.secondary.active.border,
      backgroundColor: theme.customColors.button.secondary.active.bg,
      color: theme.customColors.button.secondary.active.text,
    },
    '&:disabled': {
      ...disableUserSelect,
      cursor: 'not-allowed',
      color: `${theme.customColors.button.secondary.disabled.text} !important`,
      backgroundColor: `${theme.customColors.button.secondary.disabled.bg} !important`,
      borderColor: `${theme.customColors.button.secondary.disabled.border} !important`,
    },
  },

  // coloring
  buttonPrimary: {
    backgroundRepeat: 'repeat-x',
    backgroundSize: 'auto 100%',
    borderColor: theme.customColors.button.primary.default.border,
    backgroundColor: theme.customColors.button.primary.default.bg,
    color: theme.customColors.button.primary.default.text,

    '&:hover': {
      borderColor: theme.customColors.button.primary.hover.border,
      backgroundColor: theme.customColors.button.primary.hover.bg,
      color: theme.customColors.button.primary.hover.text,
    },
    '&:focus': {
      borderColor: theme.customColors.button.primary.focus.border,
      backgroundColor: theme.customColors.button.primary.focus.bg,
      color: theme.customColors.button.primary.focus.text,
    },
    '&:active': {
      borderColor: theme.customColors.button.primary.active.border,
      backgroundColor: theme.customColors.button.primary.active.bg,
      color: theme.customColors.button.primary.active.text,
    },
    '&:disabled': {
      color: `${theme.customColors.button.primary.disabled.text} !important`,
      backgroundColor: `${theme.customColors.button.primary.disabled.bg} !important`,
      borderColor: `${theme.customColors.button.primary.disabled.border} !important`,
    },
  },

  // loading state
  buttonLoading: {
    paddingRight: '56px !important',
    ...disableUserSelect,
    cursor: 'not-allowed',
    color: `${theme.customColors.button.secondary.disabled.text} !important`,
    backgroundColor: `${theme.customColors.button.secondary.disabled.bg} !important`,
    borderColor: `${theme.customColors.button.secondary.disabled.border} !important`,

    '&::after': {
      content: "''",
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 24,
      display: 'block',
      maskRepeat: 'no-repeat',
      right: 20,
      maskPosition: 'center',
      maskSize: '24px 24px',
      maskImage: `url(${IconSpinner})`,
      backgroundColor: `${theme.customColors.button.secondary.disabled.text} !important`,
      animation: '$rotating 3000ms linear',
      animationIterationCount: 'infinite',
      animationFillMode: 'both',
    },
  },

  // full width mode
  fullWidth: {
    width: '100%',
    textAlign: 'center',
  },

  borderless: {
    border: '2px solid transparent',
  },
}))
